export enum FocusComponent {
    MailCompose,
    MailList,
    ReadingPane,
}

const componentPriorityList: FocusComponent[] = [
    FocusComponent.MailCompose, // highest priority
    FocusComponent.MailList,
    FocusComponent.ReadingPane,
];

const registeredComponents: { [focusComponent: string]: () => boolean } = {};

/**
 * Register a component as available to be focused on
 */
export function registerComponent(
    component: FocusComponent,
    setFocusCallback: () => boolean
): void {
    const componentKey = getFocusComponentKey(component);

    // There can be 2 reading panes when openening SxS
    if (registeredComponents[componentKey] && component !== FocusComponent.ReadingPane) {
        throw new Error('Component has already registered a focus manager callback');
    }
    registeredComponents[componentKey] = setFocusCallback;
}

/**
 * Unregister a component as no longer available to be focused on
 */
export function unregisterComponent(component: FocusComponent): void {
    registeredComponents[getFocusComponentKey(component)] = null;
}

/**
 * Determine which component should get focus and executes its callback to set focus
 * @returns promise that resolves when focus operation has been performed
 */
export function resetFocus(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        window.requestAnimationFrame(function() {
            for (const component of componentPriorityList) {
                const setFocusCallback = registeredComponents[getFocusComponentKey(component)];

                // When we find a registered component in the priority list,
                // execute its set focus callback until focus() on the target element succeeds
                if (setFocusCallback && setFocusCallback()) {
                    return resolve();
                }
            }

            return resolve();
        });
    });
}

function getFocusComponentKey(component: FocusComponent): string {
    return FocusComponent[component];
}

/**
 * Sets focus to the component synchronously. This should be used with GREAT CAUTION as it will cause an immediate repaint.
 * An example is when a component is focused and about to be unmounted and focus has to be set to particular component, use
 * this function. Otherwise use resetFocus which will set focus to the highest order component.
 */
export function setFocusToSynchronous(component: FocusComponent) {
    const setFocusCallback = registeredComponents[getFocusComponentKey(component)];
    if (setFocusCallback) {
        setFocusCallback();
    }
}
